/**
 * `humanbehavior-js/auto` - the bundled half of the auto-updating install.
 *
 * This code is frozen inside the customer's build the moment they deploy, so it
 * deliberately does almost nothing: create the queue, inject the CDN loader,
 * hand back a proxy. Every decision that might need to change later (which
 * recorder build, canary split, rollback) is made by `/v1/loader.js`, which we
 * republish without the customer redeploying.
 *
 * Customers who cannot load remote scripts import `humanbehavior-js` instead,
 * which is still the fully bundled recorder.
 */

import { DEFAULT_CDN_URL, getLoaderGlobal, type LoaderGlobal } from './globals';
import { abandonQueue, createInertProxy, createTrackerProxy } from './proxy';

export interface AutoInitOptions extends Record<string, unknown> {
    /** Serve the loader and recorder from your own origin or a reverse proxy. */
    cdnUrl?: string;
    /** Pin an exact recorder build. Omit to follow the auto-updating channel. */
    version?: string;
}

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

/**
 * How long to wait before deciding the recorder is not coming.
 *
 * `onerror` covers a request that fails, but not a request that succeeds with
 * something that is not our script - a captive portal or corporate proxy
 * serving an HTML interstitial, or a CDN misconfigured to return an index page
 * for a missing key. In that case `onload` fires, no loader code runs, and
 * without this every queued call stays pending for the life of the page. A
 * customer who awaits one of our calls would be waiting forever, which turns
 * our outage into theirs.
 *
 * Long enough that a slow connection finishing normally never trips it. If it
 * does trip early, the recorder can still arrive afterwards and take over: the
 * proxy prefers a live tracker over the failed flag.
 */
const RECORDER_TIMEOUT_MS = 15000;

function injectLoaderScript(cdnUrl: string, g: LoaderGlobal): void {
    const script = document.createElement('script');
    script.async = true;
    script.src = cdnUrl.replace(/\/$/, '') + '/v1/loader.js';
    // No crossOrigin attribute on purpose: it would make delivery depend on the
    // CDN sending CORS headers, turning a header misconfiguration into "no
    // recording anywhere". The cost is opaque `Script error.` reporting.
    script.onerror = () => abandonQueue(g);
    (document.head || document.documentElement).appendChild(script);

    setTimeout(() => {
        if (!g.real && !g.failed) abandonQueue(g);
    }, RECORDER_TIMEOUT_MS);
}

/**
 * Drop-in replacement for the bundled `HumanBehaviorTracker`. Same call shape,
 * except the recording code arrives from the CDN instead of the customer's
 * bundle, so SDK fixes reach their users on the next page load.
 */
export const HumanBehaviorTracker = {
    init(apiKey: string, options: AutoInitOptions = {}): Record<string, any> {
        if (!isBrowser) return createInertProxy();

        const g = getLoaderGlobal();
        if (g.cfg) return createTrackerProxy(g);

        const { cdnUrl, version, ...trackerOptions } = options;
        g.cfg = {
            apiKey,
            options: trackerOptions,
            cdnUrl: cdnUrl || DEFAULT_CDN_URL,
            version,
        };
        injectLoaderScript(g.cfg.cdnUrl, g);
        return createTrackerProxy(g);
    },
};

export default HumanBehaviorTracker;
